import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import ApiURL from "../Api/Api";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import { SidebarContext } from "./SidebarContext";
import TransactionProofImage from "./TransactionProofImage";

const CompanyOwnerReturnReport = () => {
  const [returnRequests, setReturnRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(null);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [searchStatus, setSearchStatus] = useState("");
  const [searchStartDate, setSearchStartDate] = useState("");
  const [searchEndDate, setSearchEndDate] = useState("");
  const { isSidebarOpen, screenWidth } = useContext(SidebarContext);
  const user_id = localStorage.getItem("id");
  const role = localStorage.getItem("role");

  const fetchReturnRequests = async () => {
    if (role !== "company_owner" || !user_id) {
      setError("Access denied");
      toast.error("Access denied");
      return;
    }

    setIsLoading(true);
    try {
      const url = new URL(`${ApiURL}/getAdminReturnRequests.php`);
      url.searchParams.append("company_owner_id", user_id);

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status === "success") {
        const sortedRequests = (data.requests || []).sort((a, b) => b.id - a.id);
        setReturnRequests(sortedRequests);
        setError(null);
      } else {
        throw new Error(data.message || "Failed to load return requests");
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError("Failed to fetch return requests: " + err.message);
      toast.error("Failed to fetch return requests: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = (requestId, action, reason = "") => {
    setShowConfirmModal({ requestId, action, reason });
  };

  const confirmAction = async () => {
    const { requestId, action, reason } = showConfirmModal;
    
    try {
      const response = await fetch(`${ApiURL}/manageAdminReturnRequest.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          request_id: parseInt(requestId),
          action,
          rejection_reason: reason,
          company_owner_id: parseInt(user_id),
        }),
      });

      const text = await response.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (parseErr) {
        console.warn('manageAdminReturnRequest returned non-JSON response:', text, parseErr);
      }
      
      if (!response.ok) {
        throw new Error(data?.message || `Server error (${response.status})${text ? `: ${text}` : ''}`);
      }
      
      if (data?.status === "success") {
        toast.success(`Request ${action}d successfully`);
        await fetchReturnRequests();
        setSelectedRequest(null);
        setRejectionReason("");
        setShowConfirmModal(null);
        setExpandedRequest(null);
      } else {
        throw new Error(data?.message || `Failed to ${action} request`);
      }
    } catch (err) {
      console.error('Action error:', err);
      toast.error(`Failed to ${action} request: ` + err.message);
    }
  };

  const toggleDetails = (requestId) => {
    setExpandedRequest(expandedRequest === requestId ? null : requestId);
  };

  const getFilteredRequests = () => {
    return returnRequests.filter((req) => {
      const matchesStatus = searchStatus ? req.status === searchStatus : true;
      const reqDate = new Date(req.created_at);
      const startDate = searchStartDate ? new Date(searchStartDate) : null;
      const endDate = searchEndDate ? new Date(searchEndDate) : null;
      const matchesDateRange =
        (!startDate || reqDate >= startDate) &&
        (!endDate || reqDate <= new Date(endDate.setHours(23, 59, 59, 999)));
      return matchesStatus && matchesDateRange;
    });
  };

  const exportToCSV = () => {
    const filtered = getFilteredRequests();
    if (filtered.length === 0) {
      toast.warn("No return requests to export.");
      return;
    }

    const headers = [
      "Request ID",
      "Admin Name",
      "Date",
      "Amount",
      "Purpose",
      "Status",
      "Rejection Reason",
      "Return Type",
      "Transaction Count",
      "Transaction Nos",
      "Created At",
      "Updated At"
    ];

    const rows = filtered.map((req) => [
      `"${req.id}"`,
      `"${req.admin_name || "-"}"`,
      `"${formatDate(req.created_at)}"`,
      `"${formatCurrency(req.total_return_amount)}"`,
      `"${req.purpose || "-"}"`,
      `"${req.status || "-"}"`,
      `"${req.rejection_reason || "-"}"`,
      `"${req.return_type || "-"}"`,
      `"${req.transaction_count || 0}"`,
      `"${req.transactions?.map(t => t.transaction_no).join("; ") || "-"}"`,
      `"${formatDateTime(req.created_at)}"`,
      `"${req.updated_at ? formatDateTime(req.updated_at) : "-"}"`,
    ]);

    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `admin_return_requests_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (role === "company_owner") {
      fetchReturnRequests();
    } else {
      toast.error("Access denied. Company owner only.");
    }
  }, [role]);

  const formatCurrency = (value) => {
    return value != null ? `₹${Number(value).toLocaleString("en-IN")}` : "₹0";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN");
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-IN");
  };

  const filteredRequests = getFilteredRequests();

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 to-blue-100 pt-16 font-sans">
      <Navbar />
      <div className="flex flex-1 relative">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`min-w-0 flex-1 min-h-screen p-2 sm:p-3 overflow-y-auto transition-all duration-300 ${
            isSidebarOpen ? "lg:ml-72" : "ml-0"
          } ${isSidebarOpen && screenWidth < 640 ? "opacity-50 pointer-events-none" : ""}`}
        >
          <div className="p-4 border-b border-gray-200 mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-500">
              Admin Return Requests
            </h1>
            <p className="text-center text-gray-600 mt-2 text-sm">
              Review and approve/reject return requests from admins
            </p>
          </div>

          {/* Search and Filter */}
          <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <select
                value={searchStatus}
                onChange={(e) => setSearchStatus(e.target.value)}
                className="p-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <input
                type="date"
                value={searchStartDate}
                onChange={(e) => setSearchStartDate(e.target.value)}
                className="p-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="Start Date"
              />
              <input
                type="date"
                value={searchEndDate}
                onChange={(e) => setSearchEndDate(e.target.value)}
                className="p-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="End Date"
              />
              <button
                onClick={exportToCSV}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
                disabled={filteredRequests.length === 0}
              >
                Export to CSV
              </button>
            </div>
          </div>

          {error && (
            <div className="text-center py-3 text-red-500 text-sm">{error}</div>
          )}

          {isLoading ? (
            <div className="text-center py-6 text-gray-500 animate-pulse text-sm">Loading return requests...</div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No return requests found</div>
          ) : (
            <div className="overflow-x-auto shadow-md rounded-md">
              <table className="min-w-[1200px] w-full text-xs text-left text-gray-700 border-collapse">
                <thead className="text-xs text-white uppercase bg-gradient-to-r from-indigo-500 to-blue-500 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2">Request ID</th>
                    <th className="px-3 py-2">Admin Name</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Amount</th>
                    <th className="px-3 py-2">Particulars</th>
                    <th className="px-3 py-2">Purpose</th>
                    <th className="px-3 py-2">Proof</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Return Type</th>
                    <th className="px-3 py-2">Created At</th>
                    <th className="px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map((req) => (
                    <React.Fragment key={req.id}>
                      <tr
                        className={`border-b hover:bg-gray-50 ${
                          req.status === "pending" ? "bg-yellow-50" :
                          req.status === "approved" ? "bg-green-50" : "bg-red-50"
                        }`}
                      >
                        <td className="px-3 py-2 font-medium">{req.id}</td>
                        <td className="px-3 py-2">{req.admin_name || "-"}</td>
                        <td className="px-3 py-2">{formatDate(req.created_at)}</td>
                        <td className="px-3 py-2 font-semibold">{formatCurrency(req.total_return_amount)}</td>
                        <td className="px-3 py-2">
                          {req.transaction_count > 1 ? (
                            <button
                              onClick={() => toggleDetails(req.id)}
                              className="text-blue-500 hover:underline text-xs"
                            >
                              View {req.transaction_count} Transactions
                            </button>
                          ) : (
                            req.transactions?.[0]?.particuler_to || "-"
                          )}
                        </td>
                        <td className="px-3 py-2">{req.purpose || "-"}</td>
                        <td className="px-3 py-2"><TransactionProofImage path={req.attachment_path || req.transactions?.[0]?.attachment_path} /></td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            req.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                            req.status === "approved" ? "bg-green-100 text-green-700" :
                            "bg-red-100 text-red-700"
                          }`}>
                            {req.status || "pending"}
                          </span>
                        </td>
                        <td className="px-3 py-2">{req.return_type || "-"}</td>
                        <td className="px-3 py-2">{formatDateTime(req.created_at)}</td>
                        <td className="px-3 py-2">
                          {req.status === "pending" && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleAction(req.id, "approve")}
                                className="px-3 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => setSelectedRequest(req.id)}
                                className="px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                          {req.status !== "pending" && req.rejection_reason && (
                            <span className="text-xs text-gray-500" title={req.rejection_reason}>
                              Rejected
                            </span>
                          )}
                        </td>
                      </tr>
                      {req.transaction_count > 1 && expandedRequest === req.id && (
                        <tr className="bg-gray-50">
                          <td colSpan={11} className="px-3 py-2">
                            <div className="pl-4">
                              <h4 className="text-xs font-semibold mb-2">Transaction Details</h4>
                              <table className="w-full text-xs">
                                <thead className="bg-gray-200">
                                  <tr>
                                    <th className="px-2 py-1">Transaction No</th>
                                    <th className="px-2 py-1">Amount</th>
                                    <th className="px-2 py-1">Date</th>
                                    <th className="px-2 py-1">Particulars</th>
                                    <th className="px-2 py-1">Site</th>
                                    <th className="px-2 py-1">Main Head</th>
                                    <th className="px-2 py-1">Sub Head</th>
                                    <th className="px-2 py-1">Proof</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(req.transactions || []).map((txn) => (
                                    <tr key={txn.transaction_no} className="border-b">
                                      <td className="px-2 py-1">{txn.transaction_no}</td>
                                      <td className="px-2 py-1">{formatCurrency(txn.return_amount)}</td>
                                      <td className="px-2 py-1">{formatDate(txn.transaction_date)}</td>
                                      <td className="px-2 py-1">{txn.particuler_to || "-"}</td>
                                      <td className="px-2 py-1">{txn.site || "-"}</td>
                                      <td className="px-2 py-1">{txn.main_head || "-"}</td>
                                      <td className="px-2 py-1">{txn.sub_head || "-"}</td>
                                      <td className="px-2 py-1"><TransactionProofImage path={txn.attachment_path || req.attachment_path} /></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Rejection Modal */}
          {selectedRequest && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
                <h2 className="text-lg font-semibold mb-4">Reject Return Request</h2>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter reason for rejection"
                  rows="4"
                  required
                />
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setSelectedRequest(null)}
                    className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleAction(selectedRequest, "reject", rejectionReason)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    disabled={!rejectionReason.trim()}
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Confirm Modal */}
          {showConfirmModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
                <h2 className="text-lg font-semibold mb-4">
                  Confirm {showConfirmModal.action === "approve" ? "Approval" : "Rejection"}
                </h2>
                <p className="text-sm mb-4">
                  Are you sure you want to {showConfirmModal.action} this return request?
                  {showConfirmModal.action === "reject" && (
                    <span className="block mt-2 text-red-600"> Reason: {showConfirmModal.reason}</span>
                  )}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowConfirmModal(null)}
                    className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmAction}
                    className={`px-4 py-2 text-white rounded-lg ${
                      showConfirmModal.action === "approve" ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"
                    }`}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.main>
      </div>
    </div>
  );
};

export default CompanyOwnerReturnReport;
